import React from "react"
import {
	Accordion, AccordionDetails, AccordionSummary, Box, Chip, Typography,
	Table, TableBody, TableCell, TableHead, TableRow,
} from "@mui/material"
import { IconSeeker } from "@ui-seeker/core"
import { AppButtonIcon, AppCheckCrossIcon } from "@components"

// Group an array into a Map keyed by keyFn(item), preserving insertion order.
const groupBy = (arr, keyFn) => {
	const map = new Map()
	for (const item of arr) {
		const key = keyFn(item)
		if (!map.has(key)) map.set(key, [])
		map.get(key).push(item)
	}
	return map
}

const centered = v => <Box sx={{ display: 'flex', justifyContent: 'center' }}>{v ?? ''}</Box>

// Per-level visual style: subtle summary tint (fading with depth) + a left-border
// accent that darkens toward the top level, so hierarchy reads at a glance.
const LEVELS = [
	{ summaryBg: 'primary.lighter', accent: 'primary.dark' },     // Report
	{ summaryBg: 'background.lightGrey', accent: 'primary.main' }, // Execution
	{ summaryBg: 'background.paper', accent: 'primary.light' },    // Tool
]

// Reusable accordion node for a grouping level.
function GroupNode({ level, icon, title, count, countLabel, children }) {
	const { summaryBg, accent } = LEVELS[level] ?? LEVELS[LEVELS.length - 1]
	return (
		<Accordion
			disableGutters
			elevation={0}
			TransitionProps={{ unmountOnExit: true }}
			sx={{
				backgroundColor: 'background.paper',
				border: '1px solid', borderColor: 'divider',
				borderLeft: '3px solid', borderLeftColor: accent,
				borderRadius: 1, '&:before': { display: 'none' }, mb: 0.75,
				'&.Mui-expanded': { backgroundColor: 'background.paper' },
			}}
		>
			<AccordionSummary expandIcon={<IconSeeker icon="chevron-down"/>} sx={{ backgroundColor: summaryBg }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
					<IconSeeker icon={icon} sx={{ color: accent }}/>
					<Typography variant="body2" sx={{ fontWeight: 600 }}>{title}</Typography>
					<Box sx={{ flexGrow: 1 }}/>
					<Chip
						size="small"
						label={`${count} ${countLabel}`}
						variant="outlined"
						sx={{ backgroundColor: 'background.paper', borderColor: accent }}
					/>
				</Box>
			</AccordionSummary>
			<AccordionDetails sx={{ backgroundColor: 'background.paper', pl: 2, pr: 1, py: 1 }}>{children}</AccordionDetails>
		</Accordion>
	)
}

// Leaf: the remaining info once grouped by report → tool → execution.
function LeafTable({ rows, onOpenNotifications }) {
	return (
		<Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
		<Table
			size="small"
			sx={{
				backgroundColor: 'background.paper',
				'& .MuiTableCell-root': { borderColor: 'divider' },
				'& thead .MuiTableCell-root': { backgroundColor: 'primary.dark', color: 'common.white', fontWeight: 600 },
				'& tbody .MuiTableCell-root': { color: 'text.primary' },
				'& tbody tr:nth-of-type(even)': { backgroundColor: 'background.lightGrey' },
				'& tbody tr:hover': { backgroundColor: 'primary.lighter' },
			}}
		>
			<TableHead>
				<TableRow>
					<TableCell align="center">Office</TableCell>
					<TableCell align="center">Checked</TableCell>
					<TableCell align="center">Num Notifications</TableCell>
					<TableCell align="center">Exported</TableCell>
					<TableCell align="center">Warning</TableCell>
				</TableRow>
			</TableHead>
			<TableBody>
				{rows.map((row, i) => (
					<TableRow key={i} hover>
						<TableCell align="center">{row.idOfficePickupName ?? ''}</TableCell>
						<TableCell align="center">{centered(row.checked
							? <AppButtonIcon
								icon="check"
								tooltip="View notifications"
								sx={{ color: 'success.main' }}
								onClick={() => onOpenNotifications(row)}
							/>
							: <AppCheckCrossIcon condition={false}/>)}</TableCell>
						<TableCell align="center">{row.numNotifications ?? 0}</TableCell>
						<TableCell align="center">{centered(row.exported && row.exportUrl
							? <AppButtonIcon
								icon="file-arrow-down"
								tooltip="Download export"
								sx={{ color: 'primary.main' }}
								onClick={() => window.open(row.exportUrl, '_blank')}
							/>
							: <AppCheckCrossIcon condition={!!row.exported}/>)}</TableCell>
							<TableCell align="center">{centered(row.warning
								? <IconSeeker
									icon="triangle-exclamation"
									tooltip={(row.warningReasons ?? []).join(', ')}
									sx={{ color: 'warning.main' }}
								/>
								: '')}</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
		</Box>
	)
}

export default function GroupedCheckers({ data, onOpenNotifications }) {
	const byReport = groupBy(data, r => r.idReport)

	if (!data.length) return <Typography variant="body2" sx={{ p: 2 }}>No data available.</Typography>

	return (
		<Box>
			{[...byReport.entries()].map(([idReport, reportRows]) => {
				const byExecution = groupBy(reportRows, r => r.idReportExecution)
				return (
					<GroupNode
						key={idReport}
						level={0}
						icon="file-lines"
						title={reportRows[0]?.reportName || 'Report'}
						count={byExecution.size}
						countLabel="executions"
					>
						{[...byExecution.entries()].map(([idReportExecution, execRows]) => {
							const byTool = groupBy(execRows, r => r.toolId)
							return (
								<GroupNode
									key={idReportExecution}
									level={1}
									icon="clock"
									title={execRows[0]?.executionTime || 'Execution'}
									count={byTool.size}
									countLabel="tools"
								>
									{[...byTool.entries()].map(([toolId, toolRows]) => (
										<GroupNode
											key={toolId}
											level={2}
											icon="wrench"
											title={toolRows[0]?.toolName || 'Pricing Tool'}
											count={toolRows.length}
											countLabel="offices"
										>
											<LeafTable rows={toolRows} onOpenNotifications={onOpenNotifications}/>
										</GroupNode>
									))}
								</GroupNode>
							)
						})}
					</GroupNode>
				)
			})}
		</Box>
	)
}